import { html, type Html, View } from 'rune-ts';
import { MyPageCommissionDetailWidget } from '@marpple/mco-membership-ui';
import { NumericString } from '../../../../../features/Membership/type';
import { EXCLUDE_MAKER_MODULE } from '../../../../../shared/constant';
import { ConfigSentryF } from '../../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MembershipErrorEnable } from '../../../../../features/Membership/error/error_enable';
import { MShopUtilF } from '../../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { getActivatedSdkWithEvent } from '../../../../../features/Membership/util';
import { MembershipApiS, MembershipUtilS } from '../../../../../features/Membership/outbound/share';
import { onContactButtonClick } from '../../../../../features/Membership/util/contact';
import klass from './MyCommissionDetail.module.scss';
import { BaseLayoutOptions } from '../../../../../features/Core/service';
import { navigateTo } from '../../../../../shared/app/util';
import { makeEmptyScreenNavigate } from '../../../../../shared/app/navigate';
import { head } from '@fxts/core';
import { loadingEnd } from '../../../../../shared/client/global';
import { downloadFileFromUrl } from '../../../../../shared/util/front';
import { Alert } from '../../../../../shared/components/cells/Alert/Alert';
import { ensureImageExtension } from '@services/MarppleShop/shared/client/file.ts';

export interface MyCommissionDetailData {
  membership_token: string;
  commissionRequestId: string;
  project_domain_name_map: Record<NumericString, string>;
  layout_options: BaseLayoutOptions;
}

export class MyCommissionDetail extends View<MyCommissionDetailData> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  get isMobile(): boolean {
    return this.data.layout_options.is_mobile;
  }

  get isApp(): boolean {
    return MShopUtilF.isApp();
  }

  override async onRender() {
    try {
      const client = await getActivatedSdkWithEvent(this.data.membership_token);
      const widget = new MyPageCommissionDetailWidget({
        client,
        commissionRequestId: this.data.commissionRequestId,
        isMobile: this.isMobile,
        toggleSpinner: MembershipUtilS.toggleSpinner,
        onGoBackToCommissionListButtonClick: this._onGoBackToCommissionListButtonClick.bind(this),
        onContactButtonClick: onContactButtonClick({
          type: 'COMMISSION',
          commission_request_id: this.data.commissionRequestId,
          validationHook: this._commissionQuestionExistValidationHook.bind(this),
        }),
        onError: this._membershipErrorEnable.onError,
        isApp: this.isApp,
        onScrollLock: MShopUtilF.bodyFixed$,
        onFileDownloadButtonClick: this._onFileDownloadButtonClick.bind(this),
        onShopButtonClick: this._onShopButtonClick.bind(this),
      });

      const sdk_container_el = this.element().querySelector<HTMLElement>(`.${klass.sdk_container}`);

      if (sdk_container_el) {
        widget.render(sdk_container_el);
      } else {
        console.error('sdk를 렌더링할 엘리먼트가 존재하지 않습니다.');
      }
    } catch (e) {
      ConfigSentryF.error(e);
      await this._membershipErrorEnable.openWidgetRenderErrorModal();
    }
  }

  protected override template(): Html {
    return html` <div class="${klass.root} ${EXCLUDE_MAKER_MODULE}">
      <div class="${klass.sdk_container}"></div>
    </div>`;
  }

  private _onShopButtonClick(shopUrl: string) {
    navigateTo(shopUrl, makeEmptyScreenNavigate);
  }

  private _onGoBackToCommissionListButtonClick() {
    navigateTo(`/${ET.lang}/@/my_memberships?tab=commission`, makeEmptyScreenNavigate);
  }

  async _onFileDownloadButtonClick(url: string, fileName: string) {
    await downloadFileFromUrl({
      url,
      file_name: ensureImageExtension(fileName),
      onError: async (error) => {
        error.name = 'MyCommissionDetail._onFileDownloadButtonClick';
        ConfigSentryF.error(error);
        console.error('Download failed:', error);
        await Alert.open({ title: '다운로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' });
      },
      is_app: MShopUtilF.isCreatorApp(),
    });
  }

  private async _commissionQuestionExistValidationHook() {
    const questions = await MembershipApiS.getCommissionQuestion(this.data.commissionRequestId);
    const question = head(questions.data);

    if (question) {
      loadingEnd();
      navigateTo(`/${ET.lang}/@/my_question/${question.id}`, makeEmptyScreenNavigate);
      return true;
    }
    return false;
  }
}
